// src/modules/app-views/_apps/appsindico/units/pages/Document/tabs/PetsTab.jsx
// *******************************************************************************
// PETS TAB
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect } from 'react';
import {
  Table,
  Button,
  Flex,
  Popconfirm,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Tag,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useDocumentContext } from 'components/core/DocumentProvider';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import translateX from 'utils/translateX';
import dayjs from 'dayjs';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const SPECIES_OPTIONS = ['dog', 'cat', 'bird', 'hamster', 'other'].map(s => ({
  value: s,
  label: translateX(s),
}));

const SIZE_OPTIONS = ['small', 'medium', 'large'].map(s => ({
  value: s,
  label: translateX(s),
}));

const GENDER_OPTIONS = [
  { value: 'male', label: translateX('male') },
  { value: 'female', label: translateX('female') },
];

const SPECIES_LABEL = {
  dog: 'Dog',
  cat: 'Cat',
  bird: 'Bird',
  hamster: 'Hamster',
  other: 'Other',
};

const SPECIES_COLOR = {
  dog: 'blue',
  cat: 'orange',
  bird: 'green',
  hamster: 'purple',
  other: 'default',
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const PetsTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data, _id, refresh } = useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pets, setPets] = useState([]);
  const [selectedSpecies, setSelectedSpecies] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      if (!data?.petIds?.length) {
        setPets([]);
        return;
      }
      const promises = data.petIds.map(id =>
        axiosService.get({ url: `/v1/pets/${id}` }).catch(() => [null])
      );
      const results = await Promise.all(promises);
      setPets(results.map(r => r?.[0]).filter(Boolean));
    };
    load();
  }, [data?.petIds]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAdd = useCallback(() => {
    form.resetFields();
    setSelectedSpecies(null);
    setShowModal(true);
  }, [form]);

  const handleSpeciesChange = useCallback(value => {
    setSelectedSpecies(value);
    // Clear size field when species changes away from dog
    if (value !== 'dog') {
      form.setFieldValue('size', undefined);
    }
  }, [form]);

  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Convert dayjs dates to ISO strings for backend
      const payload = { ...values, unitId: _id };
      if (payload.birthdate) payload.birthdate = payload.birthdate.toISOString();
      if (payload.lastVaccinationAt) payload.lastVaccinationAt = payload.lastVaccinationAt.toISOString();

      const [pet] = await axiosService.post({
        url: '/v1/pets',
        data: payload,
      });

      if (pet?._id) {
        const updatedIds = [...(data?.petIds || []), pet._id];
        await axiosService.put({
          url: `/v1/units/${_id}`,
          data: { petIds: updatedIds },
        });
        refresh?.();
        setShowModal(false);
        appService.message({ flag: 's', text: 'saved_successfully' });
      }
    } catch (error) {
      if (!error?.errorFields) {
        appService.message({ flag: 'e', text: 'error_saving' });
      }
    } finally {
      setLoading(false);
    }
  }, [form, data?.petIds, _id, refresh]);

  const handleRemove = useCallback(
    async petId => {
      setLoading(true);
      try {
        const updatedIds = (data?.petIds || []).filter(id => id !== petId);
        await axiosService.put({
          url: `/v1/units/${_id}`,
          data: { petIds: updatedIds },
        });
        await axiosService.delete({ url: '/v1/pets', _id: petId });
        refresh?.();
        appService.message({ flag: 's', text: 'removed_successfully' });
      } catch {
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [data?.petIds, _id, refresh]
  );

  const handleModalCancel = useCallback(() => {
    setShowModal(false);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: translateX('name'),
      dataIndex: ['name'],
      render: (text, record) => (
        <Flex align="center" gap={8}>
          {record.species && (
            <Tag color={SPECIES_COLOR[record.species] || 'default'} style={{ margin: 0 }}>
              {SPECIES_LABEL[record.species] || translateX(record.species)}
            </Tag>
          )}
          <span>{text || '-'}</span>
        </Flex>
      ),
    },
    {
      title: translateX('breed'),
      dataIndex: ['breedId'],
      render: text => text || '-',
    },
    {
      title: translateX('color'),
      dataIndex: ['color'],
      width: 140,
      render: text => text || '-',
    },
    {
      title: translateX('actions'),
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Popconfirm title={translateX('confirm_delete')} onConfirm={() => handleRemove(record._id)}>
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={loading}
          />
        </Popconfirm>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={pets}
        pagination={false}
        loading={loading}
        size="small"
      />

      <Flex justify="end" className="mt-3">
        <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={loading}>
          {translateX('add_pet')}
        </Button>
      </Flex>

      <Modal
        title={translateX('add_pet')}
        open={showModal}
        onCancel={handleModalCancel}
        onOk={handleSave}
        confirmLoading={loading}
        destroyOnHidden
        width={560}
      >
        <Form form={form} layout="vertical" className="mt-3">
          <Flex gap={12}>
            <Form.Item
              name="name"
              label={translateX('name')}
              style={{ flex: 1 }}
              rules={[{ required: true, message: () => translateX('required_field') }]}
            >
              <Input placeholder={translateX('name')} />
            </Form.Item>
            <Form.Item
              name="species"
              label={translateX('species')}
              style={{ width: 160 }}
            >
              <Select
                options={SPECIES_OPTIONS}
                placeholder={translateX('select')}
                allowClear
                onChange={handleSpeciesChange}
              />
            </Form.Item>
          </Flex>

          <Flex gap={12}>
            <Form.Item
              name="breedId"
              label={translateX('breed')}
              style={{ flex: 1 }}
            >
              <Input placeholder={translateX('breed')} />
            </Form.Item>
            {selectedSpecies === 'dog' && (
              <Form.Item
                name="size"
                label={translateX('size')}
                style={{ width: 160 }}
              >
                <Select
                  options={SIZE_OPTIONS}
                  placeholder={translateX('select')}
                  allowClear
                />
              </Form.Item>
            )}
          </Flex>

          <Flex gap={12}>
            <Form.Item
              name="color"
              label={translateX('color')}
              style={{ flex: 1 }}
            >
              <Input placeholder={translateX('color')} />
            </Form.Item>
            <Form.Item
              name="gender"
              label={translateX('gender')}
              style={{ width: 160 }}
            >
              <Select
                options={GENDER_OPTIONS}
                placeholder={translateX('select')}
                allowClear
              />
            </Form.Item>
          </Flex>

          <Flex gap={12}>
            <Form.Item
              name="birthdate"
              label={translateX('birthdate')}
              style={{ flex: 1 }}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabledDate={disableFutureDates}
                format="DD/MM/YYYY"
              />
            </Form.Item>
            <Form.Item
              name="vaccinationCard"
              label={translateX('vaccination_card')}
              style={{ flex: 1 }}
            >
              <Input placeholder={translateX('vaccination_card')} />
            </Form.Item>
          </Flex>

          <Form.Item
            name="lastVaccinationAt"
            label={translateX('last_vaccination_at')}
            style={{ width: '50%', paddingRight: 6 }}
          >
            <DatePicker
              style={{ width: '100%' }}
              disabledDate={disableFutureDates}
              format="DD/MM/YYYY"
            />
          </Form.Item>

          <Form.Item name="observation" label={translateX('observation')}>
            <Input.TextArea rows={3} placeholder={translateX('observation')} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PetsTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function disableFutureDates(current) {
  return current && current > dayjs().endOf('day');
}
